import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import Modal from '../components/Modal';

// Modal ainda usado para abrir rodada
import './Rodadas.css';

/**
 * Página de Rodadas com abertura e fechamento
 * Conforme doc 03-regras-de-negocio-core.md e 05-especificacoes-de-api.md
 */

const Rodadas = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [rodadas, setRodadas] = useState([]);
  const [jogadores, setJogadores] = useState([]);
  const [confirmacoes, setConfirmacoes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal de abertura
  const [showAbrirModal, setShowAbrirModal] = useState(false);
  const [novaRodada, setNovaRodada] = useState({
    data: new Date().toISOString().split('T')[0],
    custo_janta: '',
    jackpot_ativado: false,
  });


  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [rodadasRes, jogadoresRes] = await Promise.all([
        apiClient.get('/rodadas'),
        apiClient.get('/jogadores'),
      ]);
      setRodadas(Array.isArray(rodadasRes.data) ? rodadasRes.data : []);
      const jogadoresList = Array.isArray(jogadoresRes.data) ? jogadoresRes.data : [];
      // Incluir todos os jogadores ativos OU admins (mesmo se inativos, para permitir adicionar admin na rodada)
      setJogadores(jogadoresList.filter(j => j && (j.ativo || j.is_admin)));
      
      // Carregar confirmações de presença para rodadas abertas
      const confirmacoesObj = {};
      for (const rodada of rodadasRes.data) {
        if (rodada.status === 'aberta') {
          try {
            const confirmRes = await apiClient.get(`/rodadas/${rodada.id}/confirmacoes`);
            confirmacoesObj[rodada.id] = confirmRes.data.map(c => c.jogador_id);
          } catch (err) {
            // Ignorar erro se não tiver confirmações
          }
        }
      }
      setConfirmacoes(confirmacoesObj);
      
      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPresenca = async (rodadaId) => {
    try {
      await apiClient.post(`/rodadas/${rodadaId}/confirmar-presenca`);
      // Atualizar lista de confirmações
      const confirmRes = await apiClient.get(`/rodadas/${rodadaId}/confirmacoes`);
      setConfirmacoes({
        ...confirmacoes,
        [rodadaId]: confirmRes.data.map(c => c.jogador_id),
      });
      carregarDados(); // Recarregar para atualizar contador
    } catch (err) {
      console.error('Erro ao confirmar presença:', err);
      alert(err.response?.data?.error || 'Erro ao confirmar presença');
    }
  };

  const handleReabrir = async (rodadaId) => {
    if (!window.confirm('Deseja reabrir esta rodada? As participações antigas serão removidas.')) return;
    
    try {
      await apiClient.put(`/rodadas/${rodadaId}/reabrir`);
      carregarDados();
      alert('Rodada reaberta com sucesso!');
    } catch (err) {
      console.error('Erro ao reabrir rodada:', err);
      alert(err.response?.data?.error || 'Erro ao reabrir rodada');
    }
  };

  const handleRemoverJogador = async (rodadaId, jogadorId) => {
    const jogador = jogadores.find(j => j.id === jogadorId);
    const nomeJogador = jogador?.nome || 'este jogador';
    
    if (!window.confirm(`Deseja remover ${nomeJogador} desta rodada?\n\nEsta ação irá cancelar a confirmação de presença do jogador.`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/rodadas/${rodadaId}/jogador/${jogadorId}`);
      carregarDados();
      alert(`${nomeJogador} removido da rodada com sucesso!`);
    } catch (err) {
      console.error('Erro ao remover jogador:', err);
      alert(err.response?.data?.error || 'Erro ao remover jogador');
    }
  };

  const handleExcluir = async (rodadaId) => {
    const rodadaEncontrada = rodadas.find(r => r.id === rodadaId);
    const mensagem = rodadaEncontrada?.status === 'fechada' 
      ? `⚠️ ATENÇÃO: Esta rodada está FECHADA. Deseja realmente excluí-la?\n\nTodas as participações, nocautes e dados relacionados serão PERMANENTEMENTE deletados.\n\nEsta ação NÃO pode ser desfeita!`
      : `Deseja realmente excluir esta rodada?\n\nTodas as participações e dados relacionados serão PERMANENTEMENTE deletados.\n\nEsta ação NÃO pode ser desfeita!`;
    
    if (!window.confirm(mensagem)) return;
    
    // Confirmação dupla para rodadas fechadas
    if (rodadaEncontrada?.status === 'fechada') {
      if (!window.confirm('⚠️ CONFIRMAÇÃO FINAL: Você tem CERTEZA que deseja excluir esta rodada FECHADA? Todos os dados serão PERDIDOS permanentemente!')) {
        return;
      }
    }
    
    try {
      await apiClient.delete(`/rodadas/${rodadaId}`);
      carregarDados();
      alert('Rodada excluída com sucesso!');
    } catch (err) {
      console.error('Erro ao excluir rodada:', err);
      alert(err.response?.data?.error || 'Erro ao excluir rodada');
    }
  };

  const handleAbrir = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/rodadas', {
        data: novaRodada.data,
        custo_janta: novaRodada.custo_janta ? parseFloat(novaRodada.custo_janta) : null,
        jackpot_ativado: novaRodada.jackpot_ativado,
      });
      setShowAbrirModal(false);
      setNovaRodada({ data: new Date().toISOString().split('T')[0], custo_janta: '', jackpot_ativado: false });
      carregarDados();
    } catch (err) {
      console.error('Erro ao abrir rodada:', err);
      alert(err.response?.data?.error || 'Erro ao abrir rodada');
    }
  };

  const iniciarFechamento = (rodada) => {
    // Navegar para página completa de fechar rodada
    navigate(`/rodadas/${rodada.id}/fechar`);
  };


  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>🎲 Rodadas</h1>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowAbrirModal(true)}>
            + Nova Rodada
          </button>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="rodadas-list">
        {rodadas.map((rodada) => (
          <div key={rodada.id} className="rodada-card">
            <div className="rodada-header">
              <h3>Rodada {new Date(rodada.data).toLocaleDateString('pt-BR')}</h3>
              <span className={`badge ${rodada.status === 'aberta' ? 'badge-success' : 'badge-secondary'}`}>
                {rodada.status === 'aberta' ? 'Aberta' : 'Fechada'}
              </span>
            </div>
            
            <div className="rodada-info">
              <p><strong>Custo Janta:</strong> {rodada.custo_janta ? `R$ ${parseFloat(rodada.custo_janta).toFixed(2)}` : 'N/A'}</p>
              <p><strong>Jackpot:</strong> {rodada.jackpot_ativado ? 'Sim' : 'Não'}</p>
              <p><strong>Participantes Confirmados:</strong> {confirmacoes[rodada.id]?.length || 0}</p>
              {rodada.status === 'fechada' && (
                <p><strong>Participantes Finais:</strong> {rodada.participacoes?.length || 0}</p>
              )}
            </div>

            {/* Lista de participantes confirmados para rodadas abertas - Melhorada visibilidade */}
            {isAdmin && rodada.status === 'aberta' && confirmacoes[rodada.id] && confirmacoes[rodada.id].length > 0 && (
              <div className="rodada-participantes" style={{ 
                marginTop: '20px', 
                marginBottom: '15px',
                padding: '15px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h4 style={{ marginTop: '0', marginBottom: '15px', color: '#333' }}>
                  👥 Participantes Confirmados ({confirmacoes[rodada.id].length})
                </h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {confirmacoes[rodada.id].map((jogadorId) => {
                    const jogador = jogadores.find(j => j.id === jogadorId);
                    if (!jogador) return null;
                    return (
                      <div key={jogadorId} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px',
                        background: 'white',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <span style={{ fontWeight: '500' }}>{jogador.nome}</span>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoverJogador(rodada.id, jogadorId)}
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '0.9em',
                            fontWeight: 'bold'
                          }}
                          title="Remover jogador da rodada"
                        >
                          🗑️ Remover
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {rodada.status === 'aberta' && (
              <div className="rodada-actions">
                {!isAdmin && (
                  <button
                    className={`btn ${confirmacoes[rodada.id]?.includes(user.id) ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => navigate(`/rodadas/${rodada.id}/confirmar`)}
                  >
                    {confirmacoes[rodada.id]?.includes(user.id) ? '✓ Presença Confirmada' : 'Confirmar Presença (R$ 65)'}
                  </button>
                )}
                {isAdmin && (
                  <>
                    <button className="btn btn-success" onClick={() => iniciarFechamento(rodada)}>
                      Fechar Rodada
                    </button>
                    <button className="btn btn-danger" onClick={() => handleExcluir(rodada.id)}>
                      🗑️ Excluir
                    </button>
                  </>
                )}
              </div>
            )}

            {isAdmin && rodada.status === 'fechada' && (
              <div className="rodada-actions">
                <button className="btn btn-secondary" onClick={() => iniciarFechamento(rodada)}>
                  Editar/Refazer Fechamento
                </button>
                <button className="btn btn-warning" onClick={() => handleReabrir(rodada.id)}>
                  Reabrir Rodada
                </button>
                <button className="btn btn-danger" onClick={() => handleExcluir(rodada.id)}>
                  🗑️ Excluir Rodada
                </button>
              </div>
            )}


            {rodada.status === 'fechada' && rodada.participacoes && (
              <details className="rodada-detalhes">
                <summary>Ver Resultados</summary>
                <table className="table-resultados">
                  <thead>
                    <tr>
                      <th>Pos</th>
                      <th>Jogador</th>
                      <th>Saldo</th>
                      <th>Pontos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rodada.participacoes
                      .sort((a, b) => a.posicao_final - b.posicao_final)
                      .map((p) => (
                        <tr key={p.id}>
                          <td>{p.posicao_final}º</td>
                          <td>{p.jogador?.nome}</td>
                          <td className={parseFloat(p.saldo_final) >= 0 ? 'saldo-positivo' : 'saldo-negativo'}>
                            R$ {parseFloat(p.saldo_final).toFixed(2)}
                          </td>
                          <td>{p.pontos_ganhos}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        ))}
      </div>

      {/* Modal de Abertura */}
      <Modal isOpen={showAbrirModal} onClose={() => setShowAbrirModal(false)} title="Nova Rodada">
        <form onSubmit={handleAbrir}>
          <div className="form-group">
            <label>Data</label>
            <input
              type="date"
              value={novaRodada.data}
              onChange={(e) => setNovaRodada({ ...novaRodada, data: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Custo da Janta (opcional)</label>
            <input
              type="number"
              step="0.01"
              value={novaRodada.custo_janta}
              onChange={(e) => setNovaRodada({ ...novaRodada, custo_janta: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={novaRodada.jackpot_ativado}
                onChange={(e) => setNovaRodada({ ...novaRodada, jackpot_ativado: e.target.checked })}
              />
              {' '}Ativar Jackpot
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setShowAbrirModal(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              Abrir Rodada
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Rodadas;
